import React from 'react';
import { Input, Tag } from "antd";
import { useTranslation } from "react-i18next";
import { sortBy } from 'lodash';

interface Category {
    id: string;
    name: string;
}

interface ProjectTag {
    id: string;
    name: string;
}

interface FiltersTabProps {
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    categories?: Category[];
    categoryFilter?: string[];
    setCategoryFilter?: (ids: string[]) => void;
    frequencies?: string[];
    frequencyFilter?: string[];
    setFrequencyFilter?: (ids: string[]) => void;
    tags?: ProjectTag[];
    usedTags?: string[];
    tagFilter?: string[];
    setTagFilter?: (tags: string[]) => void;
    [key: string]: any;
}

export const FiltersTab: React.FC<FiltersTabProps> = (props) => {
    const { t } = useTranslation();
    const categories = sortBy(props.categories || [], 'name');
    const categoryFilter = props.categoryFilter || [];
    const frequencies = sortBy(props.frequencies || []);
    const frequencyFilter = props.frequencyFilter || [];
    const tags = props.tags || [];
    const usedTags = props.usedTags || [];
    const tagFilter = props.tagFilter || [];

    const NONE = '__none__';

    const toggleCategory = (id: string) => {
        categoryFilter.includes(id)
            ? props.setCategoryFilter?.(categoryFilter.filter((c) => c !== id))
            : props.setCategoryFilter?.([...categoryFilter, id]);
    };

    const toggleFrequency = (val: string) => {
        frequencyFilter.includes(val)
            ? props.setFrequencyFilter?.(frequencyFilter.filter((f) => f !== val))
            : props.setFrequencyFilter?.([...frequencyFilter, val]);
    };

    const usedTagsMap = Object.fromEntries(usedTags.map(id => [id, true]));

    return (
        <div className="FiltersTab">
            <Input
                onChange={(e) => props.setSearchTerm(e.target.value)}
                value={props.searchTerm}
                placeholder={t('messages.search')}
                className="SearchInput w-100 mt-05"
            />

            {props.setTagFilter && (
                <>
                    <strong className="text-left mt-2" style={{ marginBottom: '0.5rem' }}>
                        {t('pages.navigator.all_tags')}
                    </strong>
                    <div className="text-left" style={{ marginBottom: '0.5rem' }}>
                        {tags.length === 0 ? (
                            <div style={{ color: 'var(--ant-color-text-tertiary)', fontSize: '0.85rem' }}>
                                {t('pages.navigator.no_tag')}
                            </div>
                        ) : (
                            tags.map((tag) => (
                                <Tag.CheckableTag
                                    key={tag.id}
                                    checked={tagFilter.includes(tag.id)}
                                    onClick={() => {
                                        const next = tagFilter.includes(tag.id)
                                            ? tagFilter.filter(id => id !== tag.id)
                                            : [...tagFilter, tag.id];
                                        props.setTagFilter!(next);
                                    }}
                                    className="tag"
                                >
                                    {tag.name}
                                </Tag.CheckableTag>
                            ))
                        )}
                    </div>
                    <div className="mt-2"></div>
                </>
            )}

            {categories.length > 0 && (
                <>
                    <strong className="text-left mt-2" style={{ marginBottom: '0.5rem' }}>
                        {t('models.indicator_category.name_plural')}
                    </strong>
                    <div className="text-left">
                        <Tag.CheckableTag
                            key="cat-none"
                            checked={categoryFilter.includes(NONE)}
                            onClick={() => toggleCategory(NONE)}
                            className="tag"
                        >
                            {t('models.indicator_category.messages.unassigned')}
                        </Tag.CheckableTag>
                        {categories.map((cat, index) => (
                            <Tag.CheckableTag
                                key={'cat' + index}
                                checked={categoryFilter.includes(cat.id)}
                                onClick={() => toggleCategory(cat.id)}
                                className="tag"
                            >
                                {cat.name}
                            </Tag.CheckableTag>
                        ))}
                    </div>
                </>
            )}
            <>
                <strong className="text-left mt-2" style={{ marginBottom: '0.5rem' }}>
                    {t('models.indicator.fields.frequency')}
                </strong>
                <div className="text-left">
                    <Tag.CheckableTag
                        key="freq-none"
                        checked={frequencyFilter.includes(NONE)}
                        onClick={() => toggleFrequency(NONE)}
                        className="tag"
                    >
                        {t('models.indicator_category.messages.unassigned')}
                    </Tag.CheckableTag>
                    {frequencies.map((freq, index) => (
                        <Tag.CheckableTag
                            key={'freq' + index}
                            checked={frequencyFilter.includes(freq)}
                            onClick={() => toggleFrequency(freq)}
                            className="tag"
                        >
                            {freq}
                        </Tag.CheckableTag>
                    ))}
                </div>
            </>
        </div>
    );
};
